/**
 * Seller review queue (pending seller verification) — shared client state.
 *
 * One tiny store that the navbar bell, the sidebar chip and the Sellers page
 * all read, so "how many sellers are waiting?" has one answer everywhere in
 * the dashboard. The navbar owns the data (hooks/useSellerReviewQueue.js
 * fetches GET /seller/review-queue and subscribes to the `seller:reviewQueue`
 * socket event); everything else only subscribes.
 *
 * The count is never incremented on the client. Every value here came from
 * the server, which re-counts the database on every change, so a reload,
 * a second tab or a missed socket frame can never leave the badge wrong.
 */

export const SELLER_REVIEW_EVENT = "seller:reviewQueue";

/** Reasons that mean "a seller just JOINED the queue" (raise an alert). */
export const SELLER_REVIEW_ARRIVAL_REASONS = Object.freeze(["REGISTERED", "SEEDED"]);

/** Reasons that mean "this seller LEFT the queue" (resolve its alert). */
export const SELLER_REVIEW_DEPARTURE_REASONS = Object.freeze([
  "VERIFIED",
  "REJECTED",
  "DEACTIVATED",
  "DELETED",
]);

/** Staff roles that review sellers; mirrors canViewSellerList on the server. */
export const SELLER_REVIEW_ROLES = Object.freeze(["SUPERUSER", "ADMIN", "SUPERVISOR"]);

/** Where the bell and the sidebar chip send the reviewer. */
export const PENDING_SELLERS_PATH = "/admin/sellers?tab=pending";

/**
 * May this signed-in account see the queue? `user` is the `result` object
 * from local storage. The server enforces the same rule; this only decides
 * whether the client bothers to ask.
 */
export function canReviewSellers(user) {
  if (!user || user.userType !== "LUSUKU STAFF") return false;
  if (user.isActive === false) return false;
  const role = String(user.user_role || user.role || "").toUpperCase();
  return SELLER_REVIEW_ROLES.includes(role);
}

const OPERATION_MODE_LABELS = {
  RESTAURANT: "Restaurant",
  RETAILER: "Retailer",
  WHOLESALER: "Wholesaler",
  HEALTHCARE: "Healthcare",
  BRAND: "Brand",
  EVENT_ORGANIZER: "Event organizer",
};

export function describeOperationMode(mode) {
  if (!mode) return "";
  const key = String(mode).toUpperCase();
  return (
    OPERATION_MODE_LABELS[key] ||
    key.toLowerCase().replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())
  );
}

/** "Shaka Zulu Foods (Restaurant)" */
export function describeSeller(seller) {
  if (!seller) return "A new seller";
  const name = seller.businessName || seller.email || "A new seller";
  const kind = describeOperationMode(seller.operationMode);
  return kind ? `${name} (${kind})` : name;
}

/** "2 sellers awaiting verification" / "1 seller awaiting verification" */
export function formatPendingSellersMessage(pending) {
  const n = Number(pending) || 0;
  return `${n} seller${n === 1 ? "" : "s"} awaiting verification`;
}

// ── Store ─────────────────────────────────────────────────────────────────────
const INITIAL = Object.freeze({
  loaded: false, // a server answer has arrived at least once
  country: null,
  pending: 0,
  sellers: [],
  at: null,
  // The last live change, so pages can react (refresh a table, raise an alert).
  lastEvent: null,
});

let state = { ...INITIAL };
const listeners = new Set();

export function getSellerReviewQueue() {
  return state;
}

function emit() {
  listeners.forEach((fn) => {
    try {
      fn(state);
    } catch (_error) {
      /* one bad subscriber must not break the others */
    }
  });
}

/** Replace the snapshot with what the server just answered. */
export function setSellerReviewQueue(snapshot = {}) {
  if (!snapshot || typeof snapshot !== "object") return state;
  state = {
    ...state,
    loaded: true,
    country: snapshot.country ?? state.country,
    pending: Number.isFinite(Number(snapshot.pending)) ? Number(snapshot.pending) : state.pending,
    sellers: Array.isArray(snapshot.sellers) ? snapshot.sellers : state.sellers,
    at: snapshot.at || new Date().toISOString(),
  };
  emit();
  return state;
}

/** Apply a live `seller:reviewQueue` frame: snapshot + the change it carried. */
export function applySellerReviewEvent(payload = {}) {
  if (!payload || typeof payload !== "object") return state;
  const reason = payload.reason || "SNAPSHOT";
  setSellerReviewQueue(payload);
  state = {
    ...state,
    lastEvent: {
      reason,
      seller: payload.seller || null,
      actorId: payload.actorId || null,
      pending: state.pending,
      at: payload.at || state.at,
      seq: (state.lastEvent?.seq || 0) + 1,
    },
  };
  emit();
  return state;
}

export function resetSellerReviewQueue() {
  state = { ...INITIAL };
  emit();
  return state;
}

export function subscribeSellerReviewQueue(fn) {
  if (typeof fn !== "function") return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function isArrivalReason(reason) {
  return SELLER_REVIEW_ARRIVAL_REASONS.includes(String(reason || "").toUpperCase());
}

export function isDepartureReason(reason) {
  return SELLER_REVIEW_DEPARTURE_REASONS.includes(String(reason || "").toUpperCase());
}
